import { useCallback, useRef, useState } from "react";
import type { EditableGeom } from "@/components/GeometryCanvas2D";

export interface GeometryHistory {
  present: EditableGeom | null;
  canUndo: boolean;
  canRedo: boolean;
  reset: (g: EditableGeom | null) => void;
  commit: (next: EditableGeom) => void;
  live: (next: EditableGeom) => void;
  beginTransient: () => void;
  undo: () => void;
  redo: () => void;
}

const MAX = 50;

export function useGeometryHistory(initial: EditableGeom | null): GeometryHistory {
  const [present, setPresent] = useState<EditableGeom | null>(initial);
  const [past, setPast] = useState<EditableGeom[]>([]);
  const [future, setFuture] = useState<EditableGeom[]>([]);
  const presentRef = useRef(present);
  presentRef.current = present;

  const reset = useCallback((g: EditableGeom | null) => {
    setPresent(g); setPast([]); setFuture([]);
  }, []);

  const commit = useCallback((next: EditableGeom) => {
    const cur = presentRef.current;
    if (cur) setPast((p) => [...p, cur].slice(-MAX));
    setFuture([]);
    setPresent(next);
  }, []);

  const live = useCallback((next: EditableGeom) => setPresent(next), []);

  const beginTransient = useCallback(() => {
    const cur = presentRef.current;
    if (cur) setPast((p) => [...p, cur].slice(-MAX));
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      const cur = presentRef.current;
      if (cur) setFuture((f) => [cur, ...f]);
      setPresent(prev);
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      const next = f[0];
      const cur = presentRef.current;
      if (cur) setPast((p) => [...p, cur].slice(-MAX));
      setPresent(next);
      return f.slice(1);
    });
  }, []);

  return {
    present, canUndo: past.length > 0, canRedo: future.length > 0,
    reset, commit, live, beginTransient, undo, redo,
  };
}
